/**
 * Utility function to trigger a browser download from a Response object
 * Handles blob creation, filename extraction, and cleanup
 */
export async function downloadFromResponse(
  response: Response,
  defaultFilename: string = 'download'
): Promise<void> {

  // Get the filename from the Content-Disposition header
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = defaultFilename;

  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  // Get the blob and create a download link
  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}

/**
 * Utility function for unauthenticated file downloads (e.g., public share links)
 * Fetches the file and triggers download
 */
export async function downloadFile(
  url: string,
  defaultFilename: string = 'download'
): Promise<void> {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Download failed: ${response.statusText}`);
  }

  await downloadFromResponse(response, defaultFilename);
}
